import { Menu } from "@mantine/core"
import StaticTable, { StaticTableProps } from "@/components/data/table/StaticTable"
import TableActionsMenu from "@/components/data/table/TableActionsMenu"
import useFetchStatic from "@/hooks/useFetchStatic"
import { MRT_Cell, MRT_CellValue, MRT_Row, MRT_TableInstance } from "mantine-react-table"
import { store } from "@/stores/stores"
import { useSnapshot } from "valtio"
import PageLayout from "./PageLayout"
import { StaticStore } from "@/lib/store-factory"
import SearchInput, { SearchInputProps } from "@/components/form/SearchInput"
import { useMemo, useState, useTransition } from "react"
import { RouteCreateEditStatic, RouteCreateEditStaticProps } from "@/components/data/table/RouteCreateEditStatic"
import DelayedRender from "@/components/utilities/DelayedRender"
import { Entity } from "@/lib/schemas"
import StaticGrid, { StaticGridProps } from "@/components/data/table/StaticGrid"
import ErrorPage from "@/components/feedback/ErrorPage"
import { cn } from "@/lib/utils"
import SearchEmpty from "../data/table/SearchEmpty"

type TableWithCreateEditProps<T extends Entity> = Partial<StaticTableProps<T>> & RouteCreateEditStaticProps<T> & StaticGridProps<T>

interface StaticLayoutProps<T extends Entity> extends TableWithCreateEditProps<T> {
  title: string
  description?: string
  headerExtra?: React.ReactNode
  extra?: React.ReactNode
  searchProps?: SearchInputProps
  searchFn?: (search: string, item: T) => boolean
  layout?: "center" | "fluid" | "center-big"
  bottomHeaderClassName?: string

  // Row Actions
  renderRowActionsExtra?: (props: {
    cell: MRT_Cell<T, MRT_CellValue>
    renderedRowIndex?: number
    row: MRT_Row<T>
    table: MRT_TableInstance<T>
  }) => React.ReactNode
}

function StaticLayout<T extends Entity>({ renderRowActionsExtra, storeName, title, ...props }: StaticLayoutProps<T>) {
  const snap = useSnapshot(store)
  useFetchStatic([storeName])
  const staticStore = snap[storeName] as StaticStore<any, any>
  const [isPending, startTransition] = useTransition()
  const loose = props.layout === "center" || props.layout === "center-big"

  const [search, setSearch] = useState("")

  const dataFiltered = useMemo<T[]>(() => {
    if (!search) return staticStore.list || []

    return (
      staticStore.list?.filter((item) => {
        if (props.searchFn) {
          return props.searchFn(search, item)
        }

        return item.name?.toLowerCase().includes(search.toLowerCase())
      }) || []
    )
  }, [staticStore.list, search])

  const isFiltering = !!search

  return (
    <PageLayout
      layout={props.layout}
      title={title}
      description={props.description}
      loading={!staticStore.fetched && staticStore.fetching}
      extra={props.headerExtra}
    >
      <div className={cn("my-2 flex items-center justify-between", loose ? "pb-6" : "px-3.5", props.bottomHeaderClassName)}>
        <SearchInput
          // autoFocus
          onChange={(e) => {
            startTransition(() => setSearch(e.target.value))
          }}
          size={loose ? "lg" : undefined}
          className={`w-full max-w-sm`}
          {...props.searchProps}
        />
        {props.extra}
      </div>
      {isFiltering && !dataFiltered.length ? (
        <div className="text-center fc flex-col">
          <SearchEmpty className="h-52 w-52" />
          <span className="-mt-2">Não encontramos nenhum registro com esses filtros...</span>
        </div>
      ) : (
        <>
          {!!staticStore.fetchedError ? (
            <ErrorPage />
          ) : (
            <DelayedRender enabled={false}>
              {loose ? (
                <StaticGrid
                  GridComponent={props.GridComponent!}
                  data={dataFiltered}
                  loading={!staticStore.fetched && staticStore.fetching}
                  isFiltering={isFiltering}
                  {...props}
                />
              ) : (
                <StaticTable<T>
                  data={dataFiltered}
                  renderRowActions={(rowProps) => (
                    <TableActionsMenu
                      items={
                        <>
                          <Menu.Item leftSection={<IconHugeiconsEdit01 className="size-5" />}>Edit</Menu.Item>
                          {renderRowActionsExtra?.(rowProps as any)}
                          <Menu.Item className="text-red-600" leftSection={<IconHugeiconsDelete03 className="size-5" />}>
                            Delete
                          </Menu.Item>
                        </>
                      }
                    />
                  )}
                  {...props}
                  columns={props.columns?.map((column, i) =>
                    i ? column : { ...column, header: `${column.header} (${staticStore.count})` },
                  )}
                />
              )}
            </DelayedRender>
          )}
        </>
      )}
      {(!!props.CreateComponent || !!props.EditComponent) && (
        <RouteCreateEditStatic<T>
          CreateComponent={props.CreateComponent}
          EditComponent={props.EditComponent}
          storeName={storeName}
          entityLabel={props.entityLabel}
          routePath={props.routePath}
        />
      )}
      {/* {!!props.createComponent && <props.createComponent storeName={storeName} />} */}
    </PageLayout>
  )
}

export default StaticLayout
